import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, catchError, filter, firstValueFrom, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './auth.service';

/** eth-api's answer to "should this user see the ETH Pulse chat widget?". */
export interface PulseWidgetSession {
  show: boolean;
  reason?: string;
  base_url?: string;
  script_url?: string;
  inbox_id?: string;
  user_token?: string;
  expires_at?: string;
  /** The organization's white-label colour (#rrggbb), when it has its own theme. */
  brand_color?: string;
}

/** eth-api's answer to "Report an issue" on a booking. */
export interface PulseBookingContext {
  show: boolean;
  reason?: string;
  context_token?: string;
  label?: string;
}

declare global {
  interface Window {
    EthPulse?: any;
    initEthPulse?: (config: { baseURL: string; inboxID: string; userJWT: string; brandColor?: string }) => any;
  }
}

/** Ask for a fresh user token this long before the current one expires. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const MIN_REFRESH_MS = 60 * 1000;
/** eth-api's reasons for no widget that mean ELKANIO PULSE is off for the whole organization. */
const INTEGRATION_OFF_REASONS = ['integration_inactive', 'organization_inactive'];

/**
 * Shows the ETH Pulse chat widget when the ELKANIO PULSE integration is active
 * for the logged-in user. eth-api decides; any error or "show: false" leaves the
 * page without a widget. Nothing from the session is written to storage.
 */
@Injectable({ providedIn: 'root' })
export class PulseWidgetService {
  private started = false;
  private accessToken: string | null = null;
  private authSub?: Subscription;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private scripts = new Map<string, Promise<void>>();
  // Bumped on every stop so late HTTP or script callbacks from an old session are ignored.
  private generation = 0;
  private activeSubject = new BehaviorSubject<boolean>(false);
  // null until eth-api has answered for the current user.
  private integrationSubject = new BehaviorSubject<boolean | null>(null);

  /** True while the chat widget is on the page for the logged-in user. */
  readonly active$: Observable<boolean> = this.activeSubject.asObservable();

  /**
   * Whether ELKANIO PULSE is on for the user's organization, once eth-api has answered. It can be
   * on while this user gets no widget (Pulse decides which roles see it), so staff still get ticket
   * status on booking pages.
   */
  readonly integrationActive$: Observable<boolean> = this.integrationSubject.pipe(
    filter((on): on is boolean => on !== null),
  );

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private zone: NgZone,
  ) {}

  /** Start for the logged-in user. Safe to call more than once. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.accessToken = this.auth.currentUser()?.access_token ?? null;

    this.authSub = this.auth.currentUsers.subscribe((user) => {
      const token = user?.access_token ?? null;
      if (token === this.accessToken) return;
      // Logged out, or a different user (e.g. proxy login) on the same page.
      this.stop();
      if (token) this.start();
    });

    this.loadSession(this.generation);
  }

  /** Remove the widget and clear its session, e.g. on logout. */
  stop(): void {
    this.generation++;
    this.started = false;
    this.accessToken = null;
    this.authSub?.unsubscribe();
    this.authSub = undefined;
    this.integrationSubject.next(null);
    this.removeWidget();
  }

  /**
   * "Report an issue" on a booking: opens the chat on a new conversation about that booking.
   * eth-api checks the booking belongs to the user's organization and fills in product, PNR and
   * travel date itself. If that fails, the chat still opens, just without the booking attached.
   */
  async reportIssue(bookingRef: string): Promise<void> {
    if (!this.activeSubject.value || !bookingRef) return;
    const gen = this.generation;
    const context = await firstValueFrom(
      this.http
        .get<PulseBookingContext>(`${environment.apiUrl}pulse/widget/booking-context`, {
          params: { ref: bookingRef.trim() },
        })
        .pipe(catchError(() => of({ show: false } as PulseBookingContext))),
    );
    if (gen !== this.generation || !this.isWidgetInstance(window.EthPulse)) return;

    this.zone.runOutsideAngular(() => {
      const widget = window.EthPulse;
      if (context?.show && context.context_token && typeof widget.startConversation === 'function') {
        widget.startConversation({ contextToken: context.context_token, label: context.label || bookingRef });
      } else {
        widget.showChat();
      }
    });
  }

  /** Open one of the user's support conversations in the widget. */
  openConversation(uuid: string): void {
    const widget = window.EthPulse;
    if (!uuid || !this.isWidgetInstance(widget) || typeof widget.openConversation !== 'function') return;
    this.zone.runOutsideAngular(() => widget.openConversation(uuid));
  }

  private loadSession(gen: number): void {
    if (!this.accessToken) {
      this.integrationSubject.next(false);
      return;
    }
    this.http.get<PulseWidgetSession>(`${environment.apiUrl}pulse/widget/session`).subscribe({
      next: (session) => {
        if (gen !== this.generation) return;
        this.integrationSubject.next(!INTEGRATION_OFF_REASONS.includes(session?.reason || ''));
        if (session?.show) this.showWidget(session, gen);
        else this.removeWidget();
      },
      // Fail closed: no widget, no error shown. Whether Pulse is on stays eth-api's call per request.
      error: () => {
        if (gen === this.generation) this.integrationSubject.next(true);
      },
    });
  }

  private showWidget(session: PulseWidgetSession, gen: number): void {
    this.loadScript(session.script_url!)
      .then(() => {
        if (gen !== this.generation || !window.initEthPulse) return;
        this.zone.runOutsideAngular(() => {
          if (this.isWidgetInstance(window.EthPulse)) {
            // Keep the open chat as it is; only future reconnects use the new token.
            window.EthPulse.config.userJWT = session.user_token;
            const widget: any = window.EthPulse;
            if ((session.brand_color || '') !== (widget.config.brandColor || '') && typeof widget.setBrandColor === 'function') {
              widget.setBrandColor(session.brand_color || '');
            }
          } else {
            window.initEthPulse!({
              baseURL: session.base_url!,
              inboxID: session.inbox_id!,
              userJWT: session.user_token!,
              brandColor: session.brand_color || '',
            });
          }
        });
        this.activeSubject.next(true);
        this.scheduleRefresh(session.expires_at!, gen);
      })
      .catch(() => {});
  }

  private scheduleRefresh(expiresAt: string, gen: number): void {
    clearTimeout(this.refreshTimer);
    const due = Date.parse(expiresAt) - Date.now() - REFRESH_MARGIN_MS;
    const delay = Number.isFinite(due) ? Math.max(due, MIN_REFRESH_MS) : MIN_REFRESH_MS;
    this.refreshTimer = setTimeout(() => {
      if (gen === this.generation) this.loadSession(gen);
    }, delay);
  }

  private removeWidget(): void {
    clearTimeout(this.refreshTimer);
    if (this.activeSubject.value) this.activeSubject.next(false);
    const widget = window.EthPulse;
    if (this.isWidgetInstance(widget)) {
      try {
        widget.logout();
        widget.destroy();
      } catch {
        // The widget is going away regardless.
      }
      // Drop the instance so a later initEthPulse creates a fresh one.
      delete window.EthPulse;
    }
  }

  // Before init, window.EthPulse is the widget class; after init it is the running instance.
  private isWidgetInstance(widget: any): boolean {
    return !!widget && typeof widget === 'object' && typeof widget.destroy === 'function';
  }

  private loadScript(src: string): Promise<void> {
    let loading = this.scripts.get(src);
    if (!loading) {
      loading = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          this.scripts.delete(src);
          script.remove();
          reject(new Error('ETH Pulse widget script failed to load'));
        };
        this.zone.runOutsideAngular(() => document.head.appendChild(script));
      });
      this.scripts.set(src, loading);
    }
    return loading;
  }
}
